/**
 * Account linking & lifecycle (mandate §1) — composed surface, DEC-065 precedent. The package drew the
 * "Link account" button and designed nothing behind it; the server has owned the whole flow since DEC-108.
 *
 * Two tabs. LINK walks provider → non-secret details → verify → done, and SAYS WHAT IT IS at every step: no
 * live broker link exists in this build, so the flow records a clearly labelled simulated shell — no password
 * is asked for, no broker is contacted, no balance is read. MANAGE lists the accounts the server serves (the
 * same authoritative rows the cards render), with reported sync health, refresh, and unlink-with-confirmation.
 * Every failure leaves a recoverable state with the server's own words.
 */
import type { ViewModel } from '@/screens/generated/view-model';
import { css } from '@pointfive/ui';

const overlay = css(
  'position:fixed;inset:0;z-index:70;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb, var(--bg) 55%, transparent);backdrop-filter:blur(2px)',
);
const sheet = css(
  'width:440px;max-width:calc(100vw - 40px);max-height:calc(100vh - 60px);overflow-y:auto;border-radius:16px;border:1px solid var(--card-line);background:var(--surface);box-shadow:var(--shadow-float);padding:18px',
);
const heading = css('font-size:13.5px;font-weight:600;margin:0');
const body = css('font-size:11.5px;color:var(--text-mid);line-height:1.6;margin:8px 0 14px');
const tabRow = css('display:flex;gap:4px;margin:12px 0 14px;border-bottom:1px solid var(--l7)');
const rowEnd = css('display:flex;gap:8px;justify-content:flex-end;margin-top:14px');
const ghostBtn = css(
  'font-size:11.5px;padding:7px 14px;border-radius:99px;border:1px solid var(--l10);background:transparent;color:var(--text-mid);cursor:pointer',
);
const primaryBtn = css(
  'font-size:11.5px;font-weight:600;padding:7px 15px;border-radius:99px;border:1px solid color-mix(in srgb, var(--slate-fill) 50%, transparent);background:color-mix(in srgb, var(--slate-fill) 10%, transparent);color:var(--slate);cursor:pointer',
);
const dangerBtn = css(
  'font-size:11px;padding:5px 11px;border-radius:99px;border:1px solid color-mix(in srgb, var(--neg) 45%, transparent);background:transparent;color:var(--neg);cursor:pointer',
);
const field = css(
  'width:100%;padding:8px 12px;border-radius:10px;border:1px solid var(--l10);background:var(--l2);color:var(--text);font-size:12px',
);
const label = css(
  'display:block;font-size:9.5px;letter-spacing:0.1em;text-transform:uppercase;color:var(--muted);margin:10px 0 5px',
);
const providerCard = css(
  'display:block;width:100%;text-align:left;padding:11px 13px;border-radius:12px;border:1px solid var(--l8);background:var(--l2);cursor:pointer;color:var(--text)',
);
const errorChip = css(
  'display:flex;align-items:center;gap:8px;margin-top:10px;padding:8px 12px;border-radius:10px;border:1px solid color-mix(in srgb, var(--neg) 40%, transparent);background:color-mix(in srgb, var(--neg) 8%, transparent);font-size:11px;color:var(--text-mid)',
);

export function AccountLink({ m }: { m: Partial<ViewModel> }) {
  const L = m.acctLinkState;
  if (!L) return null;
  const busy = L.busy;

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Link account">
      <div style={sheet}>
        <div style={css('display:flex;align-items:center;gap:10px')}>
          <h2 style={heading}>Accounts</h2>
          <div style={css('flex:1')} />
          <button style={ghostBtn} onClick={m.acctLinkClose} aria-label="Close account linking">
            Close
          </button>
        </div>

        <div style={tabRow} role="tablist">
          {(['link', 'manage'] as const).map((tab) => (
            <button
              key={tab}
              role="tab"
              aria-selected={L.tab === tab}
              onClick={() => m.acctLinkTab?.(tab)}
              style={css(
                `font-size:11.5px;padding:7px 13px;border:none;background:transparent;cursor:pointer;color:${L.tab === tab ? 'var(--text)' : 'var(--muted)'};border-bottom:2px solid ${L.tab === tab ? 'var(--slate-fill)' : 'transparent'}`,
              )}
            >
              {tab === 'link' ? 'Link new' : 'Manage linked'}
            </button>
          ))}
        </div>

        {L.tab === 'link' && L.step === 'provider' ? (
          <>
            <p style={body}>
              {/* Rail-neutral, because this dialog cannot know the server's binding: what verification
                  actually does is the SERVER's message on the next step, and it never overpromises. */}
              Pick your platform. On a live deployment, verification signs in at the broker with your own
              credentials and reads the account's real figures; a deployment without broker access says so in
              plain words instead of pretending. Nothing is stored unencrypted, ever.
            </p>
            <div style={css('display:flex;flex-direction:column;gap:8px')}>
              {(m.acctLinkProviders ?? []).map((p) => (
                <button key={p.id} style={providerCard} onClick={() => m.acctLinkPick?.(p.id)}>
                  <div style={css('font-size:12.5px;font-weight:600')}>{p.label}</div>
                  <div style={css('font-size:10.5px;color:var(--muted);margin-top:3px;line-height:1.5')}>
                    {p.blurb}
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : null}

        {L.tab === 'link' && L.step === 'form' && m.acctLinkProvider ? (
          <>
            <p style={body}>
              <b>{m.acctLinkProvider.label}</b> — {m.acctLinkProvider.blurb} A password is used for the broker
              handshake only: it is never stored raw and never appears on the link record.
            </p>
            <label style={label} htmlFor="pf-link-nickname">
              Nickname
            </label>
            <input
              id="pf-link-nickname"
              style={field}
              value={L.nickname}
              placeholder="e.g. FTMO Challenge"
              onChange={(e) => m.acctLinkSetNickname?.(e.target.value)}
            />
            {m.acctLinkProvider.fields.map((f) => (
              <div key={f.key}>
                <label style={label} htmlFor={`pf-link-${f.key}`}>
                  {f.label}
                </label>
                <input
                  id={`pf-link-${f.key}`}
                  style={field}
                  type={f.secret ? 'password' : 'text'}
                  autoComplete={f.secret ? 'off' : undefined}
                  value={(f.secret ? L.secrets[f.key] : L.params[f.key]) ?? ''}
                  placeholder={f.placeholder}
                  onChange={(e) => m.acctLinkSetParam?.(f.key, e.target.value)}
                />
                {f.hint ? (
                  <div style={css('font-size:10px;color:var(--faint);margin-top:4px')}>{f.hint}</div>
                ) : null}
              </div>
            ))}
            {L.error ? <div style={errorChip}>{L.error}</div> : null}
            <div style={rowEnd}>
              <button style={ghostBtn} onClick={m.acctLinkBack} disabled={busy}>
                Back
              </button>
              <button style={primaryBtn} onClick={m.acctLinkGo} disabled={busy} aria-label="Start the link">
                {busy ? 'Connecting…' : 'Connect'}
              </button>
            </div>
          </>
        ) : null}

        {L.tab === 'link' && L.step === 'verify' ? (
          <>
            <p style={body}>
              {/* The SERVER's message is the whole truth here — on a live rail verification contacts the
                  broker, on the sim rail it says nothing does, and this dialog must never contradict it. */}
              {L.message ?? 'The details are recorded.'} Verifying is safe to retry: the same verify resumes
              the same link and can never create a second account.
            </p>
            {L.error ? <div style={errorChip}>{L.error}</div> : null}
            <div style={rowEnd}>
              <button style={ghostBtn} onClick={m.acctLinkClose} disabled={busy}>
                Finish later
              </button>
              <button
                style={primaryBtn}
                onClick={m.acctLinkVerify}
                disabled={busy}
                aria-label="Verify the connection"
              >
                {busy ? 'Verifying…' : 'Verify connection'}
              </button>
            </div>
          </>
        ) : null}

        {L.tab === 'link' && L.step === 'done' ? (
          <>
            <p style={body}>
              <b>Recorded — as a simulated shell.</b> {L.message ?? ''}
            </p>
            <div style={rowEnd}>
              <button style={primaryBtn} onClick={m.acctLinkClose}>
                Done
              </button>
            </div>
          </>
        ) : null}

        {L.tab === 'manage' ? (
          <>
            <div style={css('display:flex;align-items:center;gap:8px;margin-bottom:10px')}>
              <p style={css('font-size:11px;color:var(--muted);margin:0;flex:1')}>
                The server’s own account list — health is reported, never assumed.
              </p>
              <button style={ghostBtn} onClick={m.acctLinkRefresh} aria-label="Refresh accounts">
                Refresh
              </button>
            </div>
            <div style={css('display:flex;flex-direction:column;gap:6px')}>
              {(m.acctLinkRows ?? []).map((a) => (
                <div
                  key={a.id}
                  style={css(
                    'display:flex;align-items:center;gap:10px;padding:9px 12px;border-radius:11px;border:1px solid var(--l7);background:var(--l2)',
                  )}
                >
                  <div style={css('flex:1;min-width:0')}>
                    <div style={css('font-size:12px;font-weight:600')}>{a.label}</div>
                    <div style={css('font-size:10px;color:var(--muted)')}>
                      {a.plan}
                      {a.simulated ? ' · simulated' : ''}
                      {a.sync ? ` · sync: ${a.sync}` : ''}
                    </div>
                  </div>
                  {L.unlinkConfirm?.id === a.id ? (
                    <>
                      <span style={css('font-size:10.5px;color:var(--neg)')}>Unlink {a.label}?</span>
                      <button style={ghostBtn} onClick={m.acctLinkUnlinkCancel} disabled={busy}>
                        Keep
                      </button>
                      <button
                        style={dangerBtn}
                        onClick={() => void m.acctLinkUnlinkGo?.(a.id, a.label)}
                        disabled={busy}
                        aria-label={`Confirm unlink ${a.label}`}
                      >
                        {busy ? 'Unlinking…' : 'Unlink'}
                      </button>
                    </>
                  ) : (
                    <button
                      style={dangerBtn}
                      onClick={() => m.acctLinkUnlinkAsk?.(a.id, a.label)}
                      aria-label={`Unlink ${a.label}`}
                    >
                      Unlink
                    </button>
                  )}
                </div>
              ))}
            </div>
            {L.error ? <div style={errorChip}>{L.error}</div> : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
